import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { Calculator, RefreshCw, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../lib/errorUtils';
import { useActiveCompany } from '../hooks/useActiveCompany';
import { currentFY, fyDropdownOptions } from '../lib/fyUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AnnualTaxWorksheet = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  // Stage 73.20k — default to the CURRENT Indian FY (Apr→Mar). Hardcoding
  // FY 2025-26 caused production confusion when an Apr-2026 payroll period
  // (statutorily FY 26-27) could only be computed against the FY 25-26
  // slabs, producing a different TDS than the Salary Register engine.
  const [fy, setFy] = useState(currentFY());
  const [worksheet, setWorksheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const downloadBulkCSV = async () => {
    setBulkBusy(true);
    try {
      const url = `${API_URL}/statutory/tds/annual-worksheet/bulk-csv?company_id=${selectedCompany}&financial_year=${fy}`;
      const response = await axios.get(url, { withCredentials: true, responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `Annual_Tax_Worksheet_Bulk_${fy}.csv`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Annual worksheet CSV downloaded');
    } catch (err) {
      const msg = err.response?.data instanceof Blob
        ? 'Bulk export failed — check that employees and CTCs are configured.'
        : formatApiError(err);
      toast.error(msg);
    } finally {
      setBulkBusy(false);
    }
  };

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data); setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
    } catch (e) { console.error(e); }
  }, [setSelectedCompany]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);
  const fetchEmployees = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/employees?company_id=${selectedCompany}`, { withCredentials: true });
      setEmployees(data);
    } catch (e) { console.error(e); }
  }, [selectedCompany]);

  useEffect(() => { if (selectedCompany) fetchEmployees(); }, [selectedCompany, fetchEmployees]);



  const fetchWorksheet = async () => {
    if (!selectedEmployee) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/statutory/tds/annual-worksheet?employee_id=${selectedEmployee}&financial_year=${fy}&company_id=${selectedCompany}`, { withCredentials: true });
      setWorksheet(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  const renderBreakdown = (result, label) => {
    if (!result) return null;
    const isRecommended = worksheet?.recommendation === label.toLowerCase().split(' ')[0];
    const isSelected = worksheet?.selected_regime === label.toLowerCase().split(' ')[0];
    return (
      <div className={`bg-white rounded-xl border-2 p-5 ${isSelected ? 'border-[#0052FF]' : 'border-slate-200'}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-slate-800">{label}</h3>
          <div className="flex space-x-2">
            {isSelected && <span className="text-xs bg-[#0052FF] text-white px-2 py-0.5 rounded-full">Active</span>}
            {isRecommended && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Best</span>}
          </div>
        </div>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between py-2 border-b border-slate-100"><span className="text-slate-500">Gross Annual Income</span><span className="font-mono font-medium">{result.gross_annual?.toLocaleString()}</span></div>
          <div className="flex justify-between py-2 border-b border-slate-100"><span className="text-slate-500">Total Deductions</span><span className="font-mono text-emerald-600">(-) {result.total_deductions?.toLocaleString()}</span></div>
          <div className="flex justify-between py-2 border-b border-slate-100"><span className="text-slate-600 font-medium">Taxable Income</span><span className="font-mono font-bold">{result.taxable_income?.toLocaleString()}</span></div>
          <div className="flex justify-between py-2 border-b border-slate-100"><span className="text-slate-500">Tax on Income</span><span className="font-mono">{result.tax_on_income?.toLocaleString()}</span></div>
          <div className="flex justify-between py-2 border-b border-slate-100"><span className="text-slate-500">Surcharge</span><span className="font-mono">{result.surcharge?.toLocaleString()}</span></div>
          <div className="flex justify-between py-2 border-b border-slate-100"><span className="text-slate-500">Health & Education Cess (4%)</span><span className="font-mono">{result.cess?.toLocaleString()}</span></div>
          <div className="flex justify-between py-3 bg-slate-50 rounded-lg px-3 -mx-1">
            <span className="font-bold text-slate-800">Total Tax Liability</span>
            <span className="font-mono font-bold text-red-600 text-base">{result.total_tax?.toLocaleString()}</span>
          </div>
          <div className="flex justify-between py-2"><span className="text-slate-500">Monthly TDS</span><span className="font-mono font-medium text-[#0052FF]">{result.monthly_tds?.toLocaleString()}</span></div>
          <div className="flex justify-between py-2"><span className="text-slate-500">Effective Tax Rate</span><span className="font-mono">{result.effective_rate}%</span></div>
        </div>
      </div>
    );
  };

  return (
    <MainLayout>
      <div data-testid="annual-tax-worksheet-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Annual Tax Worksheet</h1>
            <p className="text-sm text-slate-500 mt-1">Detailed tax computation with old vs new regime comparison for Form 16</p>
          </div>
          <div className="flex items-center space-x-3">
            <select data-testid="company-selector" value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select data-testid="employee-selector" value={selectedEmployee} onChange={(e) => setSelectedEmployee(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
              <option value="">Select Employee</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{`${e.personal_details?.first_name} ${e.personal_details?.last_name}`}</option>)}
            </select>
            <select value={fy} onChange={(e) => setFy(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="fy-selector">
              {fyDropdownOptions(2, 2).map(label => (
                <option key={label} value={label}>{`FY ${label}`}</option>
              ))}
            </select>
            <button data-testid="compute-worksheet-btn" onClick={fetchWorksheet} disabled={loading || !selectedEmployee}
              className="flex items-center space-x-2 bg-[#0052FF] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#0040CC] disabled:opacity-50">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /><span>Compute</span>
            </button>
          </div>
        </div>

        {/* Bulk export — runs the worksheet calculation for every active employee
            in this company and downloads a single CSV with OLD vs NEW totals,
            recommendation and savings per row. */}
        <div data-testid="worksheet-bulk-bar" className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex flex-wrap items-center gap-3">
          <div className="flex-1">
            <p className="text-sm font-semibold text-slate-800">Bulk worksheet — all employees</p>
            <p className="text-xs text-slate-600">Compute OLD vs NEW for every active employee in this company for FY {fy} and download as CSV.</p>
          </div>
          <button data-testid="bulk-worksheet-csv-btn" onClick={downloadBulkCSV} disabled={bulkBusy || !selectedCompany}
            className="flex items-center gap-2 bg-[#0052FF] text-white px-3 py-2 rounded-lg text-xs font-medium hover:bg-[#0040CC] disabled:opacity-50">
            {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Download Bulk CSV
          </button>
        </div>

        {worksheet && (
          <>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center"><p className="text-xs text-slate-500">Annual Income</p><p className="text-xl font-bold text-slate-900">{worksheet.annual_income?.toLocaleString()}</p></div>
                <div className="text-center"><p className="text-xs text-slate-500">Tax Savings (Best Regime)</p><p className="text-xl font-bold text-emerald-600">{worksheet.savings?.toLocaleString()}</p></div>
                <div className="text-center"><p className="text-xs text-slate-500">Recommended Regime</p><p className="text-xl font-bold text-[#0052FF] uppercase">{worksheet.recommendation}</p></div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              {renderBreakdown(worksheet.old_regime, 'Old Regime')}
              {renderBreakdown(worksheet.new_regime, 'New Regime')}
            </div>

            {worksheet.declarations && (
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <h3 className="text-sm font-semibold text-slate-800 mb-3">Declaration Summary</h3>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div><p className="text-slate-500">Status</p><p className="font-medium capitalize">{worksheet.declarations.status}</p></div>
                  <div><p className="text-slate-500">Regime Chosen</p><p className="font-medium uppercase">{worksheet.declarations.regime}</p></div>
                  <div><p className="text-slate-500">Declarations Count</p><p className="font-medium">{worksheet.declarations.sections?.length || 0}</p></div>
                </div>
              </div>
            )}
          </>
        )}

        {!worksheet && (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Calculator className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-base font-semibold text-slate-600">Annual Tax Worksheet</h3>
            <p className="text-sm text-slate-400 mt-1">Select an employee and click Compute to view the annual tax breakdown</p>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default AnnualTaxWorksheet;
